import React from "react";
import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom"; // ✅ Add this import
import "../styles/About.css";

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate(); // ✅ Navigation hook

  const features = [
    { 
      title: "Experienced Technicians", 
      desc: "9+ years of certified expertise. Factory-trained on Europe's best detailing equipment.", 
      icon: "👨‍🔧",
      stats: "5000+ cars"
    },
    { 
      title: "Genuine Parts", 
      desc: "Certified 3M, Ceramic Pro & Italian PU paints. 5+ year warranties guaranteed.", 
      icon: "✅",
      stats: "100% Authentic"
    },
    { 
      title: "24/7 Customer Support", 
      desc: "WhatsApp, Call, Live Chat. Dedicated relationship managers for every client.", 
      icon: "📞",
      stats: "Response <5min"
    },
    { 
      title: "Advanced Equipment", 
      desc: "Steam Tech, OBD Diagnostics, Nano-Ceramic Ovens. Imported from Germany & Italy.", 
      icon: "⚙️",
      stats: "Europe Standard"
    },
  ];

  // ✅ Handle booking redirect
  const handleBookService = () => {
    // Redirect to services page (adjust path as needed)
    navigate("/services");
    // Optional: Track analytics
    // gtag('event', 'book_service_click', { page: 'about' });
  };

  // Enhanced Motion Variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.15, 
        delayChildren: 0.3 
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    show: { 
      opacity: 1, 
      y: 0, 
      scale: 1, 
      transition: { 
        duration: 0.8, 
        ease: [0.22, 1, 0.36, 1],
        type: "spring"
      } 
    },
  };

  const cardHover = (index) => ({
    scale: 1.08,
    y: -15,
    boxShadow: "0 25px 50px rgba(43,50,112,0.3)",
    background: "linear-gradient(145deg, #ffffff, #f8faff)",
    transition: { 
      type: "spring", 
      stiffness: 400, 
      damping: 20 
    }
  });

  return (
    <motion.section
      className="about-page container"
      ref={ref}
      initial="hidden"
      animate={isInView ? "show" : "hidden"}
      variants={container}
    >
      {/* Trust Badge */}
      <motion.div 
        className="trust-badge"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.1 }}
      >
        <div className="badge-ring">
          <motion.div 
            className="badge-icon"
            animate={{ 
              rotate: isInView ? 360 : 0 
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "linear" 
            }}
          >
            🏆
          </motion.div>
        </div>
        <div className="badge-text">
          <h3>9+ Years Excellence</h3>
          <p>Trusted Since 2016 • 5000+ Cars</p>
        </div>
      </motion.div>

      {/* Enhanced Heading */}
      <motion.div className="hero-content" variants={item}>
        <motion.h2 
          className="about-title gradient-text" 
          variants={item}
          initial={{ scale: 0.9 }}
          whileInView={{ scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          About <span className="highlight">Chennai Car Care</span>
        </motion.h2>

        <motion.p 
          className="about-sub muted" 
          variants={item}
        >
          Chennai's Premium Car Care since 2016. From ceramic coating to PPF and complete 
          transformations using Europe's finest equipment and certified technicians.
        </motion.p>

        {/* ✅ Fixed CTA Button with Navigation */}
        <motion.button 
          className="cta-primary"
          onClick={handleBookService} // ✅ Click handler
          whileHover={{ 
            scale: 1.05, 
            boxShadow: "0 15px 35px rgba(229,57,53,0.4)" 
          }}
          whileTap={{ scale: 0.95 }}
          variants={item}
          role="button"
          aria-label="Book premium car service"
        >
          Book Premium Service →
        </motion.button>
      </motion.div>

      {/* Enhanced Features Grid */}
      <motion.div
        className="feature-cards"
        variants={container}
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
      >
        {features.map((feature, i) => (
          <motion.div
            key={i}
            className="card"
            variants={item}
            onHoverStart={() => setHoveredCard(i)}
            onHoverEnd={() => setHoveredCard(null)}
            animate={hoveredCard === i ? cardHover(i) : {}}
            whileTap={{ scale: 0.96 }}
            style={{
              background: hoveredCard === i 
                ? "linear-gradient(145deg, #ffffff, #f8faff)"
                : "transparent"
            }}
          >
            <motion.div 
              className="card-icon"
              animate={hoveredCard === i ? { 
                scale: 1.2, 
                rotate: 10 
              } : {}}
              transition={{ type: "spring", stiffness: 500 }}
            >
              {feature.icon}
            </motion.div>
            
            <motion.h4
              initial={{ opacity: 0, y: 10 }}
              animate={hoveredCard === i ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.3 }}
            >
              {feature.title}
            </motion.h4>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={hoveredCard === i ? { opacity: 1 } : { opacity: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              {feature.desc}
            </motion.p>
            
            <div className="card-stats">
              {feature.stats}
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Floating Stats */}
      <motion.div 
        className="floating-stats"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1 }}
      >
        <span>🚗 5000+</span>
        <span>⭐ 4.9/5</span>
        <span>🏭 9 Years</span>
      </motion.div>
    </motion.section>
  );
}
